import { computed } from "vue";
import { useI18n } from "vue-i18n";
import {
    stats1,
    stats2,
    stats3,
    stats4,
    type Stats1Props,
    type Stats2Props,
    type Stats3Props,
    type Stats4Props,
} from "@/views/blocks/stats/stats";
import {
    createBlock,
    arrayField,
    textField,
    headingField,
    backgroundImageField,
} from "./blockBuilder";
import type { BlockDef } from "./block.interfaces";
import { useDefaultColors } from "./useDefaultColors";
import { useFieldConfig } from "./useFieldConfig";

export function useStats() {
    const { t } = useI18n();
    const { colorDefaults } = useDefaultColors();
    const { translations } = useFieldConfig();

    const stats1Block = computed<BlockDef>(() =>
        createBlock<Stats1Props>({
            typeId: "stats1",
            type: "stats",
            name: t("page.blocks.stats.stats1"),
            component: stats1,
        })
            .withArrayField(
                "stats",
                arrayField(
                    translations.value.stat,
                    "stats",
                    {
                        statTitle: { label: translations.value.title },
                        statDesc: { label: translations.value.description },
                    },
                    [
                        {
                            statTitle: "100+",
                            statDesc: "More clients",
                        },
                        {
                            statTitle: "50+",
                            statDesc: "More projects",
                        },
                        {
                            statTitle: "1+",
                            statDesc: "More years",
                        },
                    ],
                    {
                        statTitle: "New stat",
                        statDesc: "New description",
                    }
                )
            )
            .withStyleFields([colorDefaults.value.background, colorDefaults.value.text])
            .build()
    );

    const stats2Block = computed<BlockDef>(() =>
        createBlock<Stats2Props>({
            typeId: "stats2",
            type: "stats",
            name: t("page.blocks.stats.stats2"),
            component: stats2,
        })
            .withField(
                "title",
                headingField(translations.value.title, "By the numbers", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "subtitle",
                textField(
                    translations.value.subtitle,
                    "We're proud of our achievements",
                    translations.value.text
                )
            )
            .withArrayField(
                "stats",
                arrayField(
                    translations.value.stat,
                    "stats",
                    {
                        statTitle: { label: translations.value.title },
                        statDesc: { label: translations.value.description },
                    },
                    [
                        {
                            statTitle: "8K+",
                            statDesc: "Users",
                        },
                        {
                            statTitle: "25K+",
                            statDesc: "Downloads",
                        },
                        {
                            statTitle: "98%",
                            statDesc: "Satisfaction",
                        },
                        {
                            statTitle: "12M+",
                            statDesc: "Budget",
                        },
                    ],
                    {
                        statTitle: "New stat",
                        statDesc: "New description",
                    }
                )
            )
            .withStyleFields([colorDefaults.value.background, colorDefaults.value.text])
            .build()
    );

    const stats3Block = computed<BlockDef>(() =>
        createBlock<Stats3Props>({
            typeId: "stats3",
            type: "stats",
            name: t("page.blocks.stats.stats3"),
            component: stats3,
        })
            .withField(
                "title",
                headingField(translations.value.title, "Trusted by companies worldwide", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "subtitle",
                textField(
                    translations.value.subtitle,
                    "We're proud to help organizations transform their businesses",
                    translations.value.text
                )
            )
            .withArrayField(
                "stats",
                arrayField(
                    translations.value.stat,
                    "stats",
                    {
                        statTitle: { label: translations.value.title },
                        statDesc: { label: translations.value.description },
                    },
                    [
                        {
                            statTitle: "99.9%",
                            statDesc: "Uptime guarantee",
                        },
                        {
                            statTitle: "24/7",
                            statDesc: "Support",
                        },
                        {
                            statTitle: "100K+",
                            statDesc: "Transactions",
                        },
                    ],
                    {
                        statTitle: "New stat",
                        statDesc: "New description",
                    }
                )
            )
            .withStyleFields([colorDefaults.value.background, colorDefaults.value.text])
            .build()
    );

    const stats4Block = computed<BlockDef>(() =>
        createBlock<Stats4Props>({
            typeId: "stats4",
            type: "stats",
            name: t("page.blocks.stats.stats4"),
            component: stats4,
        })
            .withField(
                "backgroundImage",
                backgroundImageField(
                    translations.value.backgroundImage,
                    {
                        src: "https://id.easy.tools/assets/images/background.jpg",
                        transparency: 10,
                    },
                    {
                        src: translations.value.src,
                        transparency: translations.value.backgroundImageTransparency,
                    }
                )
            )
            .withField(
                "topLabel",
                textField(translations.value.topLabel, "Our track record", translations.value.text)
            )
            .withField(
                "title",
                headingField(
                    translations.value.title,
                    "Trusted by thousands of customers worldwide",
                    "h2",
                    {
                        text: translations.value.text,
                        tag: translations.value.tag,
                    }
                )
            )
            .withField(
                "subtitle",
                textField(
                    translations.value.subtitle,
                    "We're proud of our achievements and the trust our clients place in us",
                    translations.value.text
                )
            )
            .withArrayField(
                "stats",
                arrayField(
                    translations.value.stat,
                    "stats",
                    {
                        statTitle: { label: translations.value.title },
                        statDesc: { label: translations.value.description },
                    },
                    [
                        {
                            statTitle: "35K+",
                            statDesc: "Customers",
                        },
                        {
                            statTitle: "40+",
                            statDesc: "Countries",
                        },
                        {
                            statTitle: "500+",
                            statDesc: "Partners",
                        },
                        {
                            statTitle: "$25M",
                            statDesc: "Revenue",
                        },
                    ],
                    {
                        statTitle: "New stat",
                        statDesc: "New description",
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.border,
            ])
            .build()
    );

    return {
        blocks: computed(() => [
            stats1Block.value,
            stats2Block.value,
            stats3Block.value,
            stats4Block.value,
        ]),
    };
}
